/**
 * 飞书错误上报 - 格式化工具
 */

import type { ErrorInfo, FeishuErrorReporterConfig } from './types';

/**
 * 格式化错误原因为可读字符串
 */
export function formatErrorReason(reason: any): string {
  if (reason === null || reason === undefined) {
    return '未知错误';
  }

  if (typeof reason === 'string') {
    return reason;
  }

  if (typeof reason === 'object') {
    // 如果是Error对象，优先使用message
    if (reason instanceof Error) {
      return reason.message || reason.toString();
    }

    // 如果有message属性，使用message
    if (reason.message) {
      return typeof reason.message === 'string' ? reason.message : JSON.stringify(reason.message);
    }

    // 如果有errMsg属性，使用errMsg
    if (reason.errMsg) {
      return typeof reason.errMsg === 'string' ? reason.errMsg : JSON.stringify(reason.errMsg);
    }

    // 如果有error属性，使用error
    if (reason.error) {
      return typeof reason.error === 'string' ? reason.error : JSON.stringify(reason.error);
    }

    // 如果有data属性，尝试使用data
    if (reason.data) {
      return typeof reason.data === 'string' ? reason.data : JSON.stringify(reason.data);
    }

    // 尝试JSON序列化
    try {
      const jsonStr = JSON.stringify(reason);
      // 如果JSON序列化结果是空对象，尝试其他方法
      if (jsonStr === '{}') {
        // 尝试获取对象的所有可枚举属性
        const keys = Object.keys(reason);
        if (keys.length > 0) {
          const keyValuePairs = keys.map((key) => `${key}: ${reason[key]}`).join(', ');
          return `{${keyValuePairs}}`;
        }
        // 如果没有可枚举属性，返回对象的构造函数名称
        return reason.constructor?.name || '未知对象错误';
      }
      return jsonStr;
    } catch {
      // JSON序列化失败时的备用方案
      try {
        // 尝试获取对象的描述性信息
        const keys = Object.keys(reason);
        if (keys.length > 0) {
          const keyValuePairs = keys
            .slice(0, 3)
            .map((key) => {
              try {
                return `${key}: ${String(reason[key])}`;
              } catch {
                return `${key}: [无法序列化]`;
              }
            })
            .join(', ');
          return `{${keyValuePairs}${keys.length > 3 ? '...' : ''}}`;
        }
        return reason.constructor?.name || '复杂对象错误';
      } catch {
        return '无法解析的错误对象';
      }
    }
  }

  // 对于其他类型（number, boolean, function等）
  try {
    return String(reason);
  } catch {
    return '无法转换的错误类型';
  }
}

/**
 * 格式化错误信息为飞书消息格式
 */
export function formatFeishuMessage(error: ErrorInfo, config?: FeishuErrorReporterConfig): any {
  // 如果提供了自定义格式化函数，使用自定义函数
  if (config?.formatError) {
    return config.formatError(error);
  }

  // 默认格式化
  return {
    msg_type: 'interactive',
    card: {
      elements: [
        {
          tag: 'div',
          text: {
            content: `🚨 **直播H5应用运行错误报告**`,
            tag: 'lark_md',
          },
        },
        {
          tag: 'hr',
        },
        {
          tag: 'div',
          fields: [
            {
              is_short: true,
              text: {
                content: `**错误类型：**\n${error.errorType}`,
                tag: 'lark_md',
              },
            },
            {
              is_short: true,
              text: {
                content: `**环境：**\n${error.environment}`,
                tag: 'lark_md',
              },
            },
            {
              is_short: true,
              text: {
                content: `**时间：**\n${new Date(error.timestamp).toLocaleString('zh-CN')}`,
                tag: 'lark_md',
              },
            },
            {
              is_short: true,
              text: {
                content: `**用户：**\n${error.username || '未知用户'} (${error.userId || 'N/A'})`,
                tag: 'lark_md',
              },
            },
            {
              is_short: true,
              text: {
                content: `**UnionId：**\n${error.unionId || 'N/A'}`,
                tag: 'lark_md',
              },
            },
          ],
        },
        {
          tag: 'div',
          text: {
            content: `**错误信息：**\n\`\`\`\n${error.message}\n\`\`\``,
            tag: 'lark_md',
          },
        },
        {
          tag: 'div',
          text: {
            content: `**页面路径：**\n${error.url}`,
            tag: 'lark_md',
          },
        },
      ].concat(
        error.stack
          ? [
              {
                tag: 'div',
                text: {
                  content: `**错误堆栈：**\n\`\`\`\n${error.stack.substring(0, 1000)}${error.stack.length > 1000 ? '...' : ''}\n\`\`\``,
                  tag: 'lark_md',
                },
              },
            ]
          : [],
      ),
    },
  };
}
